/**
 * useLlmOauth — Phase 5 Slice 5.2 hook backing the
 * `RuntimeConfigPage` "OAuth connections" + "OpenRouter / custom
 * providers" subsections.
 *
 * Reads the non-secret structural status from the new
 * `GET /api/org/:orgId/llm-oauth` endpoint (shared `llm-status-view`
 * builders — no tokens, no apiKey, no headers in the response).
 * Exposes the three lifecycle actions (init / complete / disconnect)
 * via discriminated-union return values so the page can render
 * inline errors (Manual-code OAuth flow — see `lexserver/src/routes/
 * llm-oauth.ts`).
 */
import { useCallback, useEffect, useState } from "react";
import { useOrgAdminClient } from "./useOrgAdminClient.js";
export function useLlmOauth() {
    const client = useOrgAdminClient();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await client.getLlmOauthStatus();
            setStatus(res.data);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load OAuth status");
        }
        finally {
            setLoading(false);
        }
    }, [client]);
    useEffect(() => {
        load();
    }, [load]);
    const init = useCallback(async (provider) => {
        try {
            const res = await client.initLlmOauth(provider);
            return { ok: true, data: res.data };
        }
        catch (err) {
            return {
                ok: false,
                error: err instanceof Error ? err.message : "Failed to start OAuth flow",
            };
        }
    }, [client]);
    const complete = useCallback(async (provider, flowId, code) => {
        try {
            const res = await client.completeLlmOauth(provider, { flowId, code });
            // Refresh status so the page flips to "Connected" without a manual reload.
            await load();
            return { ok: true, data: res.data };
        }
        catch (err) {
            return {
                ok: false,
                error: err instanceof Error ? err.message : "Failed to complete OAuth flow",
            };
        }
    }, [client, load]);
    const disconnect = useCallback(async (provider) => {
        try {
            const res = await client.disconnectLlmOauth(provider);
            await load();
            return { ok: true, data: { provider: res.data.provider } };
        }
        catch (err) {
            return {
                ok: false,
                error: err instanceof Error ? err.message : "Failed to disconnect provider",
            };
        }
    }, [client, load]);
    return {
        status,
        loading,
        error,
        reload: load,
        init,
        complete,
        disconnect,
    };
}
//# sourceMappingURL=useLlmOauth.js.map